import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Boxes, IndianRupee, PackageX, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import {
  DataTable,
  KpiCard,
  Panel,
  inr,
  num,
  type DataColumn,
} from "@/components/admin/analytics/AnalyticsKit";
import {
  FieldGroup,
  NotesField,
  NumberField,
  SelectField,
  TextField,
} from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  deleteStockItem,
  listInventoryCategories,
  listInventorySuppliers,
  listStockItems,
  saveStockItem,
} from "@/lib/admin/inventory.functions";
import {
  ITEM_STATUSES,
  ITEM_STATUS_LABEL,
  STOCK_STATE_LABEL,
  STORAGE_LABEL,
  STORAGE_TYPES,
  UNITS,
  stockItemSchema,
  type StockItem,
  type StockItemInput,
} from "@/lib/admin/inventory.schemas";

export const Route = createFileRoute("/_admin/admin/stock-items")({
  head: () => ({
    meta: [
      { title: "Stock Items — Admin" },
      {
        name: "description",
        content:
          "Ingredient and consumable master with SKU, stock levels, reorder points, costing, GST and storage.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StockItemsPage,
});

function emptyForm(): StockItemInput {
  return {
    sku: "",
    item_name: "",
    category_id: null,
    description: "",
    unit: "kg",
    current_stock: 0,
    minimum_stock: 0,
    maximum_stock: 0,
    reorder_level: 0,
    purchase_price_paise: 0,
    average_cost_paise: 0,
    selling_cost_paise: 0,
    preferred_supplier_id: null,
    storage_type: "dry",
    shelf_life_days: 0,
    expiry_date: null,
    hsn_code: "",
    gst_rate: 5,
    barcode: "",
    status: "active",
    remarks: "",
  };
}

const STATE_FILTERS = [
  { key: "all", label: "All" },
  { key: "low", label: "Low stock" },
  { key: "out", label: "Out of stock" },
  { key: "over", label: "Overstocked" },
] as const;

function StockItemsPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listStockItems);
  const catFn = useServerFn(listInventoryCategories);
  const supFn = useServerFn(listInventorySuppliers);
  const saveFn = useServerFn(saveStockItem);
  const deleteFn = useServerFn(deleteStockItem);

  const items = useQuery({
    queryKey: ["admin", "inventory", "items"],
    queryFn: () => listFn(),
    staleTime: 15_000,
  });
  const categories = useQuery({
    queryKey: ["admin", "inventory", "categories"],
    queryFn: () => catFn(),
    staleTime: 60_000,
  });
  const suppliers = useQuery({
    queryKey: ["admin", "inventory", "suppliers"],
    queryFn: () => supFn(),
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<StockItem | "new" | null>(null);
  const [form, setForm] = useState<StockItemInput>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<StockItem | null>(null);
  const [stateFilter, setStateFilter] = useState<(typeof STATE_FILTERS)[number]["key"]>("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing)
      setForm({
        sku: editing.sku,
        item_name: editing.item_name,
        category_id: editing.category_id,
        description: editing.description,
        unit: editing.unit as StockItemInput["unit"],
        current_stock: editing.current_stock,
        minimum_stock: editing.minimum_stock,
        maximum_stock: editing.maximum_stock,
        reorder_level: editing.reorder_level,
        purchase_price_paise: editing.purchase_price_paise,
        average_cost_paise: editing.average_cost_paise,
        selling_cost_paise: editing.selling_cost_paise,
        preferred_supplier_id: editing.preferred_supplier_id,
        storage_type: editing.storage_type as StockItemInput["storage_type"],
        shelf_life_days: editing.shelf_life_days,
        expiry_date: editing.expiry_date,
        hsn_code: editing.hsn_code,
        gst_rate: editing.gst_rate,
        barcode: editing.barcode,
        status: editing.status as StockItemInput["status"],
        remarks: editing.remarks,
      });
    setErrors({});
  }, [editing]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "inventory"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: StockItemInput }) => saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Stock item saved");
      setEditing(null);
      await invalidate();
    },
    onError: (e: Error) =>
      toast.error(e.message === "duplicate" ? "That SKU already exists." : "Couldn't save item."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Item removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove item."),
  });

  const all = useMemo(() => items.data ?? [], [items.data]);
  const rows = useMemo(
    () =>
      all.filter(
        (r) =>
          (stateFilter === "all" || r.stock_state === stateFilter) &&
          (categoryFilter === "all" || r.category_name === categoryFilter),
      ),
    [all, stateFilter, categoryFilter],
  );

  const kpi = useMemo(
    () => ({
      total: all.length,
      value: all.reduce((s, r) => s + r.stock_value_paise, 0),
      low: all.filter((r) => r.stock_state === "low").length,
      out: all.filter((r) => r.stock_state === "out").length,
    }),
    [all],
  );

  const submit = () => {
    const parsed = stockItemSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      return;
    }
    save.mutate({ id: editing === "new" || !editing ? null : editing.id, patch: parsed.data });
  };

  const columns: Array<DataColumn<StockItem>> = [
    {
      key: "sku",
      header: "SKU",
      render: (r) => <span className="font-mono text-[11px]">{r.sku}</span>,
      sortValue: (r) => r.sku,
      exportValue: (r) => r.sku,
    },
    {
      key: "item",
      header: "Ingredient",
      render: (r) => (
        <div>
          <p className="font-medium text-foreground">{r.item_name}</p>
          <p className="text-[11px] text-muted-foreground">
            {r.category_name} · {STORAGE_LABEL[r.storage_type] ?? r.storage_type}
          </p>
        </div>
      ),
      sortValue: (r) => r.item_name,
      exportValue: (r) => r.item_name,
    },
    {
      key: "stock",
      header: "Current",
      align: "right",
      render: (r) => `${r.current_stock} ${r.unit}`,
      sortValue: (r) => r.current_stock,
      exportValue: (r) => r.current_stock,
    },
    {
      key: "levels",
      header: "Min / Reorder / Max",
      align: "right",
      render: (r) => (
        <span className="text-xs text-muted-foreground">
          {r.minimum_stock} / {r.reorder_level} / {r.maximum_stock}
        </span>
      ),
      exportValue: (r) => `${r.minimum_stock}/${r.reorder_level}/${r.maximum_stock}`,
    },
    {
      key: "price",
      header: "Purchase price (₹)",
      align: "right",
      render: (r) => inr(r.purchase_price_paise / 100),
      sortValue: (r) => r.purchase_price_paise,
      exportValue: (r) => r.purchase_price_paise / 100,
    },
    {
      key: "value",
      header: "Stock value (₹)",
      align: "right",
      render: (r) => inr(r.stock_value_paise / 100),
      sortValue: (r) => r.stock_value_paise,
      exportValue: (r) => r.stock_value_paise / 100,
    },
    {
      key: "supplier",
      header: "Preferred supplier",
      render: (r) => <span className="text-xs">{r.supplier_name}</span>,
      exportValue: (r) => r.supplier_name,
    },
    {
      key: "expiry",
      header: "Expiry",
      render: (r) => (
        <span
          className={
            r.days_to_expiry !== null && r.days_to_expiry <= 7 ? "text-destructive" : undefined
          }
        >
          {r.expiry_date ?? "—"}
        </span>
      ),
      sortValue: (r) => r.expiry_date ?? "",
      exportValue: (r) => r.expiry_date ?? "",
    },
    {
      key: "state",
      header: "Stock state",
      render: (r) => (
        <Badge variant={r.stock_state === "out" ? "destructive" : "secondary"}>
          {STOCK_STATE_LABEL[r.stock_state]}
        </Badge>
      ),
      exportValue: (r) => STOCK_STATE_LABEL[r.stock_state],
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={r.status === "active" ? "secondary" : "outline"}>
          {ITEM_STATUS_LABEL[r.status] ?? r.status}
        </Badge>
      ),
      exportValue: (r) => ITEM_STATUS_LABEL[r.status] ?? r.status,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-destructive"
            onClick={() => setConfirmDelete(r)}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  const categoryOptions = [
    { value: null as string | null, label: "Uncategorised" },
    ...(categories.data ?? []).map((c) => ({ value: c.id as string | null, label: c.name })),
  ];
  const supplierOptions = [
    { value: null as string | null, label: "No preferred supplier" },
    ...(suppliers.data ?? []).map((sup) => ({
      value: sup.id as string | null,
      label: sup.supplier_name,
    })),
  ];

  return (
    <div>
      <AdminPageHeader
        title="Stock Items"
        description="The ingredient and consumable master — stock levels, reorder points, costing and storage."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label="Stock items"
          value={num(kpi.total)}
          tone="info"
          icon={Boxes}
          loading={items.isPending}
        />
        <KpiCard
          label="Inventory value"
          value={inr(kpi.value / 100)}
          tone="success"
          icon={IndianRupee}
          loading={items.isPending}
        />
        <KpiCard
          label="Low stock"
          value={num(kpi.low)}
          tone="warn"
          icon={AlertTriangle}
          loading={items.isPending}
        />
        <KpiCard
          label="Out of stock"
          value={num(kpi.out)}
          tone="danger"
          icon={PackageX}
          loading={items.isPending}
        />
      </div>

      <Panel title="Stock item master" subtitle={`${rows.length} of ${all.length} shown`}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {STATE_FILTERS.map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={stateFilter === f.key ? "default" : "outline"}
              onClick={() => setStateFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
          <div className="w-52">
            <SelectField
              label=""
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={[
                { value: "all", label: "All categories" },
                ...(categories.data ?? []).map((c) => ({ value: c.name, label: c.name })),
              ]}
            />
          </div>
          <Button size="sm" className="ml-auto gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            New stock item
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={items.isPending}
          exportName="stock-items"
          importModule="stock-items"
          pageSize={15}
          minWidth={1100}
          searchOn={(r) => `${r.item_name} ${r.sku} ${r.category_name} ${r.supplier_name}`}
        />
      </Panel>

      <Dialog open={editing !== null} onOpenChange={(o) => (o ? null : setEditing(null))}>
        <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New stock item" : "Edit stock item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <FieldGroup title="Identification">
              <TextField
                label="SKU"
                value={form.sku}
                onChange={(v) => setForm({ ...form, sku: v })}
                error={errors["sku"]}
              />
              <TextField
                label="Ingredient name"
                value={form.item_name}
                onChange={(v) => setForm({ ...form, item_name: v })}
                error={errors["item_name"]}
              />
              <SelectField
                label="Category"
                value={form.category_id ?? "none"}
                onChange={(v) => setForm({ ...form, category_id: v === "none" ? null : v })}
                options={categoryOptions.map((o) => ({ value: o.value ?? "none", label: o.label }))}
              />
              <SelectField
                label="Unit"
                value={form.unit}
                onChange={(v) => setForm({ ...form, unit: v as StockItemInput["unit"] })}
                options={UNITS.map((u) => ({ value: u, label: u }))}
              />
              <SelectField
                label="Status"
                value={form.status}
                onChange={(v) => setForm({ ...form, status: v as StockItemInput["status"] })}
                options={ITEM_STATUSES.map((v) => ({ value: v, label: ITEM_STATUS_LABEL[v] ?? v }))}
              />
              <TextField
                label="Barcode"
                value={form.barcode}
                onChange={(v) => setForm({ ...form, barcode: v })}
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                className="sm:col-span-2 lg:col-span-3"
              />
            </FieldGroup>

            <FieldGroup title="Stock levels">
              <NumberField
                label="Current stock"
                step={0.01}
                value={form.current_stock}
                onChange={(v) => setForm({ ...form, current_stock: v })}
                suffix={form.unit}
              />
              <NumberField
                label="Minimum stock"
                step={0.01}
                value={form.minimum_stock}
                onChange={(v) => setForm({ ...form, minimum_stock: v })}
                suffix={form.unit}
              />
              <NumberField
                label="Maximum stock"
                step={0.01}
                value={form.maximum_stock}
                onChange={(v) => setForm({ ...form, maximum_stock: v })}
                suffix={form.unit}
              />
              <NumberField
                label="Reorder level"
                step={0.01}
                value={form.reorder_level}
                onChange={(v) => setForm({ ...form, reorder_level: v })}
                suffix={form.unit}
              />
              <SelectField
                label="Storage type"
                value={form.storage_type}
                onChange={(v) =>
                  setForm({ ...form, storage_type: v as StockItemInput["storage_type"] })
                }
                options={STORAGE_TYPES.map((v) => ({ value: v, label: STORAGE_LABEL[v] ?? v }))}
              />
              <NumberField
                label="Shelf life"
                value={form.shelf_life_days}
                onChange={(v) => setForm({ ...form, shelf_life_days: v })}
                suffix="days"
              />
              <TextField
                label="Expiry date"
                type="date"
                value={form.expiry_date ?? ""}
                onChange={(v) => setForm({ ...form, expiry_date: v || null })}
              />
            </FieldGroup>

            <FieldGroup title="Financials (₹)">
              <NumberField
                label="Purchase price"
                value={form.purchase_price_paise / 100}
                step={0.01}
                onChange={(v) => setForm({ ...form, purchase_price_paise: Math.round(v * 100) })}
                suffix={`₹ per ${form.unit}`}
              />
              <NumberField
                label="Average cost (₹)"
                step={0.01}
                value={form.average_cost_paise / 100}
                onChange={() => {}}
                suffix={`₹ per ${form.unit}`}
              />
              <NumberField
                label="Selling price"
                value={form.selling_cost_paise / 100}
                step={0.01}
                onChange={(v) => setForm({ ...form, selling_cost_paise: Math.round(v * 100) })}
                suffix="₹ optional"
              />
              <TextField
                label="HSN code"
                value={form.hsn_code}
                onChange={(v) => setForm({ ...form, hsn_code: v })}
              />
              <NumberField
                label="GST rate (%)"
                step={0.5}
                value={form.gst_rate}
                onChange={(v) => setForm({ ...form, gst_rate: v })}
                suffix="%"
              />
              <SelectField
                label="Preferred supplier"
                value={form.preferred_supplier_id ?? "none"}
                onChange={(v) =>
                  setForm({ ...form, preferred_supplier_id: v === "none" ? null : v })
                }
                options={supplierOptions.map((o) => ({ value: o.value ?? "none", label: o.label }))}
              />
              <NotesField
                label="Remarks"
                value={form.remarks}
                onChange={(v) => setForm({ ...form, remarks: v })}
              />
            </FieldGroup>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save stock item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(o) => (o ? null : setConfirmDelete(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.item_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Historic stock movements stay in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
